<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import useLocale from '../composables/useLocale';
import useNavigationStore from '../stores/navigation';
import useBookPagination from '../composables/use-book-pagination';
import { useDisplay } from 'vuetify';
import { VNumberInput } from 'vuetify/labs/VNumberInput';
import useReaderStore from '../stores/reader';
import useUiStore from '../stores/ui';
import useNavigationTriggerStore from '../stores/navigation-trigger';
import type { GoToItem } from '../types';
import { captureSentryException } from '@ilteducation/sentry-vue3';

type Emits = { (e: 'goToContent', item: GoToItem): void };
const emit = defineEmits<Emits>();

const { pageRange } = useBookPagination();
const {
  isInitializing,
  resolutionProgress,
  isNavigating,
  hasPages,
  pages,
  currentPageIndex,
  currentPage,
  firstAndLastPageNumber,
} = storeToRefs(useNavigationStore());
const { t } = useLocale();
const { xs } = useDisplay();
const { isAudioOnly } = storeToRefs(useReaderStore());
const { setIsPageRangeEditing, setIsHoveringPageRange } = useUiStore();
const { setNavigationTrigger } = useNavigationTriggerStore();
const { isPageRangeEditing: isEditing } = storeToRefs(useUiStore());

const inputValue = ref<number | undefined>(currentPage.value?.page);
const inputRef = ref<InstanceType<typeof VNumberInput> | null>(null);

const isClickable = computed(
  () => !isAudioOnly.value && !isNavigating.value && !isInitializing.value && hasPages.value,
);

const startEditing = () => {
  if (!isClickable.value) return;
  setIsPageRangeEditing(true);
  nextTick(() => {
    inputRef.value?.focus();
  });
};

const stopEditing = () => {
  setIsPageRangeEditing(false);
};

const submitPageNavigation = () => {
  const page = inputValue.value;
  if (page == null) return;
  if (page < firstAndLastPageNumber.value.min || page > firstAndLastPageNumber.value.max) return;
  const targetPage = pages.value.find((p) => p.page.toString().includes(page.toString()));
  if (targetPage) {
    setNavigationTrigger('PAGE_INPUT');
    emit('goToContent', {
      target: targetPage.cfi || targetPage.href,
      isGoingForwardsInBook: page > currentPageIndex.value,
    });
  } else {
    captureSentryException('Target page not found for page: ' + page);
  }
  stopEditing();
};
</script>

<template>
  <div class="page-range-container d-flex align-center min-w-0 ga-3" data-testid="bp-page-range-display-btn">
    <div v-if="isEditing" v-click-outside="stopEditing">
      <v-btn
        icon
        density="compact"
        size="small"
        variant="flat"
        class="cancel-btn mb-1"
        :ripple="false"
        @click.stop="stopEditing"
        @mousedown.prevent
      >
        <v-icon size="small">mdi-close</v-icon>
      </v-btn>

      <v-number-input
        ref="inputRef"
        v-model="inputValue"
        variant="solo-filled"
        flat
        reverse
        inset
        density="compact"
        hide-details
        class="page-input"
        :class="xs ? 'text-body-2' : 'text-body-1'"
        @focus="($event.target as HTMLInputElement).select()"
        @keydown.enter="submitPageNavigation"
        @keydown.esc="stopEditing"
      >
        <template #append-inner>
          <v-btn
            icon
            density="compact"
            variant="text"
            class="navigate-btn mx-1"
            :ripple="false"
            @click.stop="submitPageNavigation"
            @mousedown.prevent
          >
            <v-icon size="small">mdi-arrow-right</v-icon>
          </v-btn>
        </template>
      </v-number-input>
    </div>

    <v-btn
      v-else
      variant="flat"
      density="compact"
      class="page-range-display d-flex align-center"
      :class="[{ 'is-pending': isNavigating || isInitializing }, xs ? 'text-body-2' : 'text-body-1']"
      :disabled="!isClickable"
      @click.stop="startEditing"
      @mouseenter="setIsHoveringPageRange(true)"
      @mouseleave="setIsHoveringPageRange(false)"
      @focusin="setIsHoveringPageRange(true)"
      @focusout="setIsHoveringPageRange(false)"
    >
      <span class="flex-shrink-0 mr-1">
        {{ pageRange.text }}
      </span>

      <template v-if="pageRange.chapter">
        <span v-if="pageRange.text" class="mx-2 opacity-60 flex-shrink-0">|</span>
        <span class="text-truncate flex-grow-1 chapter-text">
          {{ pageRange.chapter }}
        </span>
      </template>

      <div class="d-sr-only" role="status" aria-live="polite" aria-atomic="true">
        <span v-if="isNavigating || isInitializing">{{ t('book_player.label_reading_position_loading') }}</span>
        <template v-else>
          <span v-if="pageRange.text">{{ pageRange.text }}</span>
          <span v-if="pageRange.chapter">{{ pageRange.chapter }}</span>
        </template>
      </div>
    </v-btn>

    <div v-if="isInitializing" class="status-indicator flex-shrink-0">
      <v-progress-circular v-if="isInitializing" :model-value="resolutionProgress" :size="18" :width="2" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-range-container {
  // Ensures the container doesn't collapse
  height: 100%;
}

.page-range-display {
  background-color: rgba(var(--v-theme-on-surface), 0.05) !important;
  &.v-btn--disabled {
    opacity: 1 !important;
    color: rgba(var(--v-theme-on-primary)) !important;
    :deep(.v-btn__overlay),
    :deep(.v-btn__underlay) {
      display: none !important;
    }
  }
}

.cancel-btn {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(var(--v-theme-inverse-surface), 0.9) !important;
  color: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgb(var(--v-theme-outline-variant));
}

.page-input {
  height: 3rem;
  width: 14rem;

  :deep(.v-field) {
    --v-field-padding-start: 0;
    --v-field-padding-end: 0;
    background: rgba(var(--v-theme-inverse-surface), 0.9) !important;
    border: 1px solid rgb(var(--v-theme-outline-variant)) !important;
    outline: solid 1px rgb(var(--v-theme-outline-variant)) !important;
    border-radius: 9999px !important;
    align-items: center;
    overflow: hidden;
  }

  :deep(.v-field__overlay) {
    display: none;
  }

  :deep(.v-label) {
    color: rgb(var(--v-theme-surface)) !important;
    opacity: 1 !important;
    left: 0 !important;
    right: auto !important;
    transform-origin: left center !important;
  }

  :deep(.v-field__input) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-weight: inherit;
  }

  :deep(input) {
    color: rgb(var(--v-theme-surface)) !important;
    text-align: center;
  }

  :deep(.v-number-input__control) {
    display: flex;
    align-items: center;
    border: none !important;

    .v-btn {
      border-top: none !important;
      border-bottom: none !important;
    }
  }

  :deep(.v-number-input__control .v-btn),
  :deep(.navigate-btn) {
    color: rgb(var(--v-theme-surface)) !important;
    background: none !important;
    width: 2.25rem !important;
    height: 2.25rem !important;
    min-width: 2.25rem !important;
    border-radius: 50% !important;

    &:hover {
      background: rgba(var(--v-theme-surface), 0.1) !important;
    }

    .v-btn__overlay,
    .v-btn__underlay {
      display: none;
    }
  }

  :deep(.navigate-btn) {
    border: 1px solid rgba(var(--v-theme-surface), 0.4) !important;
  }
}

.is-pending {
  opacity: 0.6;
  // Optional: subtle pulse to show it's working
  animation: pulse-loading 2s infinite ease-in-out;
  pointer-events: none;
}

.chapter-text {
  max-width: clamp(120px, 45vw, 600px);
}

.opacity-60 {
  opacity: 0.6;
}

.min-w-0 {
  min-width: 0 !important;
}

@keyframes pulse-loading {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 0.6;
  }
}
</style>
